import { InspectionValues } from '@parallel-fluidics/shared/constants';
import { allocateBomConsumption } from '@parallel-fluidics/shared/models/lots';
import { travelerFromFirestore } from '@parallel-fluidics/shared/models/travelers';
import {
  doc,
  getFirestore,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';

import { BOM_NO_LONGER_COVERED, consumeLot, readLotsForConsumption } from './lotConsumption';

/**
 * Save a traveler and consume its newly installed component bags in one
 * transaction. `installs` describes, per step, the newly scanned bags (in
 * scan order) and the step's residual BOM needs (per-unit quantity minus
 * what previously saved installs already cover). Inside the transaction
 * every new bag is re-read fresh: quarantined/awaiting-inspection bags
 * fail the save, allocation re-runs against fresh remaining quantities
 * (a bag drained elsewhere fails a step that is being marked Passed
 * instead of silently under-consuming), and each bag gets a `consumed`
 * event with its allocated share, the decrement, sealed → Open, and
 * auto-Empty at zero — the same semantics as assembly. The traveler is
 * also re-read fresh: a bag already installed on the step (e.g. a
 * double-submitted save) fails instead of consuming twice, and the two
 * trace fields (per-step installedLots, root componentLotIds) are built
 * on the fresh doc rather than the caller's form state — installs only
 * ever grow, so a concurrent save's bags survive a stale caller.
 *
 * @param {object} params
 * @param {string} params.travelerId
 * @param {Array<object>} params.steps - Step objects to write (without the new installs).
 * @param {boolean} params.shipped
 * @param {Array<{
 *   stepIndex: number,
 *   newLotIds: string[],
 *   residualLines: Array<{projectDocId: string, pPartDocId: string, quantity: number}>,
 * }>} params.installs
 * @param {{uid: string, email: string}} params.user
 */
export default async function saveTravelerWithInstalls({
  travelerId,
  steps,
  shipped,
  installs,
  user,
}) {
  const db = getFirestore();
  const travelerRef = doc(db, 'travelers', travelerId);

  return runTransaction(db, async (transaction) => {
    // All reads first: the traveler, then fresh copies of every newly
    // installed bag.
    const travelerSnap = await transaction.get(travelerRef);
    if (!travelerSnap.exists()) throw new Error('Traveler not found');
    // Modeled collection — read through the shared converter so the fresh
    // doc follows the Traveler contract (defaults included).
    const freshTraveler = travelerFromFirestore(travelerSnap);
    const freshSteps = freshTraveler.steps || [];
    const alreadyInstalled = installs.find(({ stepIndex, newLotIds }) => {
      const installed = freshSteps[stepIndex]?.installedLots || [];
      return newLotIds.some((lotId) => installed.some((entry) => entry.lotId === lotId));
    });
    if (alreadyInstalled) {
      throw new Error('A scanned bag is already installed on this step — the save may have gone through already. Reload and check.');
    }

    const allNewLotIds = [...new Set(installs.flatMap((install) => install.newLotIds))];
    const newLots = await readLotsForConsumption(transaction, allNewLotIds);
    const lotById = Object.fromEntries(newLots.map((lot) => [lot.id, lot]));

    // A bag can serve several steps — track its availability across them.
    const availableByLotId = {};
    newLots.forEach((lot) => {
      availableByLotId[lot.id] = Math.max(lot.remainingQty ?? 0, 0);
    });

    // installedLots only ever grow (there is no un-install UI), so the
    // fresh doc's entries are authoritative — a concurrent save's bags
    // survive being absent from this caller's stale form state. Steps
    // without the field on either side keep omitting it (an explicit
    // undefined would fail the Firestore write).
    const newSteps = steps.map((step, index) => {
      const freshInstalled = freshSteps[index]?.installedLots ?? step.installedLots;
      return freshInstalled === undefined ? step : { ...step, installedLots: freshInstalled };
    });
    const totalDeltaByLotId = {};
    installs.forEach(({ stepIndex, newLotIds: rawLotIds, residualLines }) => {
      // Dedupe while keeping scan order — a duplicated ID would double-count
      // its take and write the same install entry twice.
      const newLotIds = [...new Set(rawLotIds)];
      const stepSources = newLotIds.map((lotId) => ({
        id: lotId,
        projectDocId: lotById[lotId].projectDocId,
        pPartDocId: lotById[lotId].pPartDocId,
        remainingQty: availableByLotId[lotId],
      }));
      const { lines, deltaByLotId } = allocateBomConsumption(1, residualLines, stepSources);

      const stepPassed = newSteps[stepIndex]?.inspected === InspectionValues.PASSED;
      if (stepPassed && lines.some((line) => line.short > 0)) {
        throw new Error(BOM_NO_LONGER_COVERED);
      }

      // A zero take still records the install: scans are the record
      // (strict graph). The common case is a bag wrongly marked Empty
      // that was physically used — its consumed event carries
      // deltaQty null (consumption untracked), like assemble's
      // not-on-BOM bags.
      const entries = newLotIds.map((lotId) => {
        const take = -(deltaByLotId[lotId] || 0);
        availableByLotId[lotId] -= take;
        totalDeltaByLotId[lotId] = (totalDeltaByLotId[lotId] || 0) - take;
        return { lotId, quantity: take };
      });
      newSteps[stepIndex] = {
        ...newSteps[stepIndex],
        installedLots: [...(newSteps[stepIndex].installedLots || []), ...entries],
      };
    });

    // Union against the fresh doc, not the form, for the same reason.
    const newComponentLotIds = [...new Set([
      ...(freshTraveler.componentLotIds || []),
      ...allNewLotIds,
    ])];
    transaction.update(travelerRef, {
      steps: newSteps,
      shipped: shipped || false,
      componentLotIds: newComponentLotIds,
      _updatedBy: {
        email: user.email,
        uid: user.uid,
        timestamp: serverTimestamp(),
      },
    });

    newLots.forEach((lot) => {
      consumeLot(transaction, {
        lot,
        deltaQty: totalDeltaByLotId[lot.id] || null,
        reason: 'Installed on traveler',
        via: 'install',
        relatedIds: { travelerId },
        user,
      });
    });

    // The trace fields as written — lets the caller reset the form
    // immediately instead of waiting for the snapshot (prevents
    // double-save and stale re-submits of componentLotIds).
    return { steps: newSteps, componentLotIds: newComponentLotIds };
  });
}
